import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Library } from "@/api/admin-api"

import { inspectSharePoint, type SpoInspectResult } from "@/api/admin/libraries_indexing"

import {
  browseItemKindFor,
  expandSharePointHostname,
  getCopyAuthCandidates,
  getLibraryAuth,
  hasStoredSecretReference,
  MASKED_SECRET_INDICATOR,
  readSpoField,
  SHAREPOINT_COPY_SOURCE_TYPES,
  type SpoCamelField,
  spoFieldStorageKey,
} from "../library-auth-utils"
import { parseSharePointUrl } from "../parse-sharepoint-url"
import {
  type ExtraDataRow,
  type ExtraDataRowErrors,
  extraDataRowsFromConfig,
  extraDataRowsToConfig,
  validateExtraDataRows,
} from "./extra-data-model"
import {
  defaultOutputNamesFor,
  type EditorSection,
  EMPTY_EDITOR_VALUES,
  isSharePointFamily,
  type LibraryEditorErrors,
  type LibraryEditorMode,
  type LibraryEditorValues,
  sectionsFor,
  slugifyLibraryName,
  sourceConfigWithKey,
  sourceConfigWithResolvedIds,
} from "./library-editor-model"
import { sectionForError, validateEditorValues } from "./library-editor-validation"
import { withFilthSourceSelection } from "./library-filth-sources-utils"
import { useFilthBackendsQuery } from "../../hooks/use-filth-backends"
import {
  type SharePointVerifyTarget,
  useSharePointConnectionVerify,
} from "../../hooks/use-sharepoint-connection-verify"

/** source_config keys that change what a connection verify actually targets. */
const VERIFY_TARGET_KEYS: ReadonlySet<string> = new Set([
  "hostname",
  "siteName",
  "listName",
  "listId",
  "siteId",
  "site_name",
  "list_name",
  "list_id",
  "site_id",
  "folderPath",
  "folder_path",
  "replicate_permissions",
])

const DEFAULT_OPEN: Record<EditorSection, boolean> = {
  basics: true,
  source: true,
  connection: false,
  content: false,
  extraData: false,
  indexing: false,
  advanced: false,
}

/** inspect-sharepoint state: ids and columns for the current target, or why it failed. */
export type LibraryInspectState = {
  status: "idle" | "loading" | "done" | "error"
  result: SpoInspectResult | null
  error: string | null
}

const IDLE_INSPECT: LibraryInspectState = { status: "idle", result: null, error: null }

type UseLibraryEditorOptions = {
  mode: LibraryEditorMode
  /** Values to (re)load the form with; the form resets whenever this reference changes. */
  initialValues: LibraryEditorValues
  /** The library being edited (edit mode); used to restore its original auth. */
  library?: Library | null
  existingLibraries: Library[]
}

/**
 * All state and behaviour behind the shared library editor: values, validation, section
 * open-state, copy-auth, and the 3-step SharePoint verify. Both dialogs consume this.
 */
export function useLibraryEditor({
  mode,
  initialValues,
  library,
  existingLibraries,
}: UseLibraryEditorOptions) {
  const { t } = useTranslation()

  const [values, setValues] = useState<LibraryEditorValues>(initialValues)
  const { data: filthBackends } = useFilthBackendsQuery(values.search_backend === "filth")
  const filthUsesPerUserSpaces =
    filthBackends?.find((backend) => backend.id === values.filth_backend_id)?.consumer_mode ===
    "primary"
  const [errors, setErrors] = useState<LibraryEditorErrors>({})
  const [openSections, setOpenSections] = useState<Record<EditorSection, boolean>>(DEFAULT_OPEN)
  const [idManuallyEdited, setIdManuallyEdited] = useState(false)
  // extra_data_definition is edited as rows (stable keys for React and drag-and-drop); the
  // stored array inside values.source_config follows every row change.
  const [extraDataRows, setExtraDataRows] = useState<ExtraDataRow[]>(() =>
    extraDataRowsFromConfig(initialValues.source_config.extra_data_definition)
  )
  const [extraDataErrors, setExtraDataErrors] = useState<Map<string, ExtraDataRowErrors>>(
    () => new Map()
  )

  const verify = useSharePointConnectionVerify()
  const resetVerify = verify.reset

  // inspect-sharepoint (ids + columns). Like verify, a result only holds for the target it was
  // run against; the generation counter drops completions of a run that was superseded.
  const [inspect, setInspect] = useState<LibraryInspectState>(IDLE_INSPECT)
  const inspectRun = useRef(0)
  const resetInspect = useCallback(() => {
    inspectRun.current += 1
    setInspect(IDLE_INSPECT)
  }, [])

  const reset = useCallback(
    (next: LibraryEditorValues) => {
      setValues(next)
      setErrors({})
      setOpenSections(DEFAULT_OPEN)
      setIdManuallyEdited(false)
      setExtraDataRows(extraDataRowsFromConfig(next.source_config.extra_data_definition))
      setExtraDataErrors(new Map())
      resetVerify()
      resetInspect()
    },
    [resetInspect, resetVerify]
  )

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const update = useCallback((patch: Partial<LibraryEditorValues>) => {
    setValues((prev) => withFilthSourceSelection(prev, patch))
  }, [])
  const setSectionOpen = useCallback((section: EditorSection, open: boolean) => {
    setOpenSections((prev) => ({ ...prev, [section]: open }))
  }, [])

  const updateSourceConfig = useCallback(
    (key: string, value: unknown) => {
      // A verified (or inspected) result only holds for the target it was run against.
      if (VERIFY_TARGET_KEYS.has(key)) {
        resetVerify()
        resetInspect()
      }
      // A name change retargets the library: the ids go with it (sourceConfigWithKey).
      setValues((prev) => ({
        ...prev,
        source_config: sourceConfigWithKey(prev.source_config, key, value),
      }))
    },
    [resetInspect, resetVerify]
  )

  /** Write a SharePoint field to whichever key shape the config already uses. */
  const updateSpoField = useCallback(
    (field: SpoCamelField, value: string | undefined) => {
      updateSourceConfig(spoFieldStorageKey(values.source_config, field), value)
    },
    [updateSourceConfig, values.source_config]
  )

  /** Nested object under source_config (list_item, pages.access_control, ...). */
  const updateNestedSourceConfig = useCallback((path: string[], key: string, value: unknown) => {
    setValues((prev) => {
      const root: Record<string, unknown> = { ...prev.source_config }
      let cursor = root
      for (const segment of path) {
        const existing = cursor[segment]
        const next =
          existing && typeof existing === "object" && !Array.isArray(existing)
            ? { ...(existing as Record<string, unknown>) }
            : {}
        cursor[segment] = next
        cursor = next
      }
      cursor[key] = value
      return { ...prev, source_config: root as LibraryEditorValues["source_config"] }
    })
  }, [])

  const updateAuth = useCallback(
    (key: "client_id" | "tenant_id" | "client_secret", value: string) => {
      resetVerify()
      setValues((prev) => ({
        ...prev,
        source_config: {
          ...prev.source_config,
          auth: { ...prev.source_config.auth, [key]: value },
        },
      }))
    },
    [resetVerify]
  )

  const handleDisplayNameChange = useCallback(
    (name: string) => {
      setValues((prev) => {
        if (mode !== "create") return { ...prev, display_name: name }
        const id = idManuallyEdited ? prev.id : slugifyLibraryName(name)
        return { ...prev, display_name: name, id, ...defaultOutputNamesFor(id) }
      })
    },
    [idManuallyEdited, mode]
  )

  const handleIdChange = useCallback((rawId: string) => {
    const id = rawId.toLowerCase()
    setIdManuallyEdited(true)
    setValues((prev) => ({ ...prev, id, ...defaultOutputNamesFor(id) }))
  }, [])

  const handleSourceTypeChange = useCallback(
    (sourceType: string) => {
      resetVerify()
      resetInspect()
      setExtraDataRows([])
      setExtraDataErrors(new Map())
      setValues((prev) => ({
        ...prev,
        source_type: sourceType,
        source_config: { ...EMPTY_EDITOR_VALUES.source_config },
        copy_auth_from_library_id: undefined,
      }))
    },
    [resetInspect, resetVerify]
  )

  const handleSharePointUrlChange = useCallback(
    (url: string) => {
      resetVerify()
      resetInspect()
      const parsed = parseSharePointUrl(url)
      setValues((prev) => ({
        ...prev,
        source_config: {
          ...prev.source_config,
          sharepoint_url: url,
          hostname: parsed?.hostname ?? undefined,
          siteName: parsed?.siteName ?? undefined,
          listName: parsed?.listName ?? undefined,
          folderPath: parsed?.folderPath ? parsed.folderPath : undefined,
          // Ids resolved for a previous URL no longer apply.
          siteId: undefined,
          listId: undefined,
          drive_id: undefined,
        },
      }))
    },
    [resetInspect, resetVerify]
  )

  // ----- extra data ------------------------------------------------------------------------
  const updateExtraDataRows = useCallback((rows: ExtraDataRow[]) => {
    setExtraDataRows(rows)
    setValues((prev) => ({
      ...prev,
      source_config: {
        ...prev.source_config,
        // No rows drops the key (the indexer defaults to an empty list); update sends it as null.
        extra_data_definition: rows.length > 0 ? extraDataRowsToConfig(rows) : undefined,
      },
    }))
  }, [])

  // ----- copy-auth -----------------------------------------------------------------------
  const isCopyAuthType = SHAREPOINT_COPY_SOURCE_TYPES.has(values.source_type)
  const hostname =
    typeof values.source_config.hostname === "string" ? values.source_config.hostname : undefined
  const copyAuthCandidates = useMemo(
    () =>
      isCopyAuthType
        ? getCopyAuthCandidates(existingLibraries, {
            hostname,
            excludeLibraryId: mode === "edit" ? library?.id : undefined,
          })
        : [],
    [existingLibraries, hostname, isCopyAuthType, library?.id, mode]
  )
  const copyAuthId = values.copy_auth_from_library_id?.trim() || undefined

  const handleCopyAuthChange = useCallback(
    (source: Library | null) => {
      resetVerify()
      if (!source) {
        // Back to manual entry: in edit, restore this library's own stored auth (masked secret
        // included); in create, just drop the copy marker and keep whatever was typed.
        const original = mode === "edit" && library ? getLibraryAuth(library) : undefined
        setValues((prev) => ({
          ...prev,
          copy_auth_from_library_id: undefined,
          source_config:
            mode === "edit"
              ? { ...prev.source_config, auth: original ? { ...original } : undefined }
              : prev.source_config,
        }))
        return
      }
      const sourceAuth = getLibraryAuth(source)
      setValues((prev) => ({
        ...prev,
        copy_auth_from_library_id: source.id,
        source_config: {
          ...prev.source_config,
          auth: {
            ...prev.source_config.auth,
            client_id: sourceAuth?.client_id?.trim() || "",
            tenant_id: sourceAuth?.tenant_id?.trim() || "",
            // Empty on purpose: the server copies the source's Key Vault reference on save.
            client_secret: "",
          },
        },
      }))
    },
    [library, mode, resetVerify]
  )

  // ----- verify --------------------------------------------------------------------------
  const auth = values.source_config.auth
  const rawSecret = auth?.client_secret ?? ""
  const typedSecret = rawSecret === MASKED_SECRET_INDICATOR ? "" : rawSecret.trim()
  // An empty field (still masked, or typed and cleared again) keeps the stored reference on save,
  // so verify can resolve it through this library's id in both states.
  const keepsStoredSecret = mode === "edit" && !typedSecret && hasStoredSecretReference(library)
  const canResolveStoredSecret = isCopyAuthType && keepsStoredSecret
  const verifyHostname = expandSharePointHostname(hostname)
  const verifySiteName = readSpoField(values.source_config, "siteName")
  const isSharePoint = isSharePointFamily(values.source_type)

  const canVerify = Boolean(
    isSharePoint &&
    verifyHostname &&
    verifySiteName &&
    auth?.client_id?.trim() &&
    auth.tenant_id?.trim() &&
    (typedSecret || copyAuthId || canResolveStoredSecret)
  )

  const buildVerifyTarget = useCallback((): SharePointVerifyTarget | null => {
    if (!verifyHostname || !verifySiteName) return null
    return {
      hostname: verifyHostname,
      siteName: verifySiteName,
      listName: readSpoField(values.source_config, "listName") || undefined,
      listId: readSpoField(values.source_config, "listId") || undefined,
      folderPath: readSpoField(values.source_config, "folderPath") || undefined,
      auth: {
        client_id: auth?.client_id ?? "",
        tenant_id: auth?.tenant_id ?? "",
        client_secret: typedSecret,
      },
      // Typed secret wins; otherwise the server resolves the copy source's, or (edit) this
      // library's own, stored reference.
      copy_auth_from_library_id:
        copyAuthId ?? (mode === "edit" && library && !typedSecret ? library.id : undefined),
      check_permissions_scope: values.source_config.replicate_permissions === true,
      itemKind: browseItemKindFor(values.source_type),
    }
  }, [
    auth,
    copyAuthId,
    library,
    mode,
    typedSecret,
    values.source_config,
    values.source_type,
    verifyHostname,
    verifySiteName,
  ])

  const handleVerify = useCallback(() => {
    const target = buildVerifyTarget()
    if (target) void verify.runVerify(target)
  }, [buildVerifyTarget, verify])

  const handleLoadMore = useCallback(() => {
    const target = buildVerifyTarget()
    if (target) void verify.loadMore(target)
  }, [buildVerifyTarget, verify])

  // ----- inspect (ids + columns) ----------------------------------------------------------
  const canInspect = canVerify

  const runInspect = useCallback(async (): Promise<SpoInspectResult | null> => {
    const target = buildVerifyTarget()
    if (!target) return null
    const run = ++inspectRun.current
    setInspect({ status: "loading", result: null, error: null })
    try {
      const result = await inspectSharePoint({
        hostname: target.hostname,
        siteName: target.siteName,
        listName: target.listName,
        listId: target.listId,
        auth: target.auth,
        copy_auth_from_library_id: target.copy_auth_from_library_id,
      })
      if (run !== inspectRun.current) return null
      setInspect({ status: "done", result, error: null })
      return result
    } catch (caught) {
      if (run !== inspectRun.current) return null
      setInspect({
        status: "error",
        result: null,
        error: caught instanceof Error ? caught.message : String(caught),
      })
      return null
    }
  }, [buildVerifyTarget])

  // Straight through setValues: the ids describe the very target verify and inspect ran
  // against, so neither result is reset.
  const applyInspectedIds = useCallback((result: SpoInspectResult) => {
    setValues((prev) => ({
      ...prev,
      source_config: sourceConfigWithResolvedIds(prev.source_config, result),
    }))
  }, [])

  const handleResolveIds = useCallback(async () => {
    const result = await runInspect()
    if (result) applyInspectedIds(result)
  }, [applyInspectedIds, runInspect])

  const authConfigured = Boolean(
    auth?.client_id && auth.tenant_id && (rawSecret || copyAuthId || keepsStoredSecret)
  )

  let secretPlaceholder = t(
    "adminDashboard.libraries.editor.secretPlaceholder",
    "Key Vault secret name"
  )
  if (copyAuthId) {
    secretPlaceholder = t(
      "adminDashboard.libraries.editor.secretCopiedPlaceholder",
      "Secret will be copied from the selected library (leave empty)"
    )
  } else if (keepsStoredSecret) {
    secretPlaceholder = t(
      "adminDashboard.libraries.editor.secretConfigured",
      "Secret is configured (leave empty to keep)"
    )
  }

  // ----- validation ----------------------------------------------------------------------
  const validate = useCallback((): boolean => {
    const next = validateEditorValues(values, { mode, filthUsesPerUserSpaces }, t)
    const rowErrors = validateExtraDataRows(extraDataRows, t)
    setExtraDataErrors(rowErrors)
    if (rowErrors.size > 0) {
      next.extra_data_definition = t(
        "adminDashboard.libraries.editor.errors.extraDataInvalid",
        "Fix the highlighted extra data fields"
      )
    }
    setErrors(next)
    const fields = Object.keys(next) as Array<keyof LibraryEditorErrors>
    if (fields.length > 0) {
      setOpenSections((prev) => {
        const opened = { ...prev }
        for (const field of fields) opened[sectionForError(field)] = true
        return opened
      })
    }
    return fields.length === 0
  }, [extraDataRows, filthUsesPerUserSpaces, mode, t, values])

  return {
    filthBackends,
    filthUsesPerUserSpaces,
    mode,
    values,
    errors,
    sections: sectionsFor(values.source_type),
    openSections,
    setSectionOpen,
    extraDataRows,
    extraDataErrors,
    updateExtraDataRows,
    update,
    updateSourceConfig,
    updateSpoField,
    updateNestedSourceConfig,
    updateAuth,
    handleDisplayNameChange,
    handleIdChange,
    handleSourceTypeChange,
    handleSharePointUrlChange,
    copyAuthCandidates,
    copyAuthId,
    isCopyAuthType,
    handleCopyAuthChange,
    verify,
    canVerify,
    handleVerify,
    handleLoadMore,
    inspect,
    canInspect,
    runInspect,
    handleResolveIds,
    authConfigured,
    secretPlaceholder,
    typedSecret,
    rawSecret,
    validate,
    reset,
  }
}

export type LibraryEditor = ReturnType<typeof useLibraryEditor>
